import useI18n from 'i18n/useI18n';
import React, { useEffect, useMemo } from 'react';
import { ITEM_STATUS } from 'utils/status';
import Filter from 'components/Filter';
import { onSelectCategory, onSelectSubcategory } from 'pages/MenuItems/utils/events';
import {
    getProcessedCategoriesWithOptions,
    getProcessedSubcategoriesWithOptions,
} from 'pages/MenuItems/utils/transformers';
import StatusFilter from 'components/StatusFilter';
import useCategories from 'state/categories';
import { filterByCategory, filterByStatus } from 'pages/MenuItems/utils/filters';
import { useSearch } from 'providers/search';
import { compareStrings } from 'utils';

import useTranslatedSelect from 'hooks/useTranslatedSelect';
import useTranslatedFiltersSelect from './useTranslatedFiltersSelect';

import styled from 'styled-components';
import useFilters from 'state/filters';

const Container = styled.div`    
    max-width: 70%;
    flex: 0 0 70%;
    display: flex;
    gap: 15px;
    margin-right: 15px;

    > div {
        min-width: 250px;
        max-width: 30%;
        flex: 0 0 30%;
    }

    @media (max-width: 768px) {
        max-width: 100%;
        flex: 0 0 100%;
        margin-right: 0px;
    }
`;

const INIT_STATUS_OPTION = {
    value: ITEM_STATUS.ACTIVE, label: 'On Menu'
};

const STATUS_OPTIONS = [
    { value: null, label: 'All Status' },
    INIT_STATUS_OPTION,
    { value: ITEM_STATUS.INACTIVE86, label: "86's no stock" },
    { value: ITEM_STATUS.INACTIVE, label: 'Inactive' },
    { value: ITEM_STATUS.ARCHIVED, label: 'Archived' },
];

const sortItemsByKey = ( key, items ) => {
    return (a, b)=> {                    
        const catA = a.categories.find(({ category }) => category === items[key][0].value);
        const catB = b.categories.find(({ category }) => category === items[key][0].value);

        if(!catA.position && !!catB.position) return 1;
        if(!!catA.position && !catB.position) return -1;
        return catA.position - catB.position;
    }
}

const Filters = ({ data }) => {
    const { translate } = useI18n();
    const [categories] = useCategories();
    
    const { search } = useSearch();
    const [statusOptions, selectedStatus, setSelectedStatus] = useTranslatedSelect(STATUS_OPTIONS, INIT_STATUS_OPTION);

    const [filters, filtersDispatcher] = useFilters();

    const [categoryOptions, categoryPlaceholder] = useTranslatedFiltersSelect(
        filters.items.categoriesOptions, 
        filters.items.selectedCategories,
        filtersDispatcher.itemsSetSelectedCategories
    );
    const [subCategoryOptions, subCategoryPlaceholder] = useTranslatedFiltersSelect(
        filters.items.subcategoriesOptions, 
        filters.items.selectedSubcategories,
        filtersDispatcher.itemsSetSelectedSubcategories
    );
    
    useEffect(() => {
        const processed = getProcessedCategoriesWithOptions(categories, {withAny: true});
        filtersDispatcher.itemsSetCategoriesOptions(processed);
    }, [categories.list, categories.subcategories]);

    useEffect(() => {
        if (filters.items.selectedCategories.length === 1 && filters.items.selectedCategories[0].value === -1) {
            filtersDispatcher.itemsSetSubcategoriesOptions(getProcessedSubcategoriesWithOptions(categories.subcategories));
            return;
        }
        const reduced = filters.items.selectedCategories.reduce((acc, curr) => [...acc, ...curr.subcategories], []);
        filtersDispatcher.itemsSetSubcategoriesOptions(getProcessedSubcategoriesWithOptions(reduced));
    }, [filters.items.selectedCategories, categories.subcategories]);

    useEffect(() => {
        const newList = data.list
            .filter((p) => filterByCategory(p, filters.items.selectedCategories, filters.items.selectedSubcategories, filters.items.categoriesOptions))
            .filter((p) => filterByStatus(p, selectedStatus))
            .filter(
                ({ translations }) => 
                Object.values(translations).some(({ title, description }) => compareStrings(title ?? '', search) || compareStrings(description ?? '', search))
            );
        
        if( filters.items.selectedCategories.length === 1 && filters.items.selectedCategories[0].value > 0 ){
            if( filters.items.selectedSubcategories.length === 1 && filters.items.selectedSubcategories[0].value === -2 ){
                newList.sort(sortItemsByKey('selectedCategories',  filters.items));
            }
            if( filters.items.selectedSubcategories.length === 1 && filters.items.selectedSubcategories[0].value > 0 ){
                newList.sort(sortItemsByKey('selectedSubcategories',  filters.items));
            }
        }

        filtersDispatcher.itemsSetList(newList);
    }, [data.list, filters.items.selectedCategories, filters.items.selectedSubcategories, selectedStatus, search]);

    const isSubcategoryDisabled = useMemo(()=>{
        if( filters.items.selectedCategories.length === 1 && filters.items.selectedCategories[0].value === -2 )
            return true;
        return false;
    },[filters.items.selectedCategories]);

    return (
        <Container>
            <Filter
                labelProps={{ label: translate('Category') }}
                selectProps={{
                    onChange: onSelectCategory(filters.items.selectedCategories, filtersDispatcher.itemsSetSelectedCategories),
                    options: categoryOptions,
                    placeholder: categoryPlaceholder,
                }}
                selectedValues={filters.items.selectedCategories}
                containerProps={{styles:{
                    height: 'fit-content'
                }}}
            />
            <Filter
                labelProps={{ label: translate('Subcategory') }}
                selectProps={{
                    onChange: onSelectSubcategory(filters.items.selectedSubcategories, filtersDispatcher.itemsSetSelectedSubcategories),
                    options: subCategoryOptions,
                    placeholder: subCategoryPlaceholder,
                    isDisabled: isSubcategoryDisabled,
                }}
                selectedValues={filters.items.selectedSubcategories}
                containerProps={{styles:{
                    height: 'fit-content'
                }}}
            />
            <StatusFilter
                labelProps={{ label: translate('Status') }}
                selectProps={{
                    onChange: (value) => {
                        setSelectedStatus(value);
                    },
                    options: statusOptions,
                    placeholder: translate(''),
                    value: selectedStatus,
                }}
                containerProps={{styles:{
                    height: 'fit-content'
                }}}
            />
        </Container>
    );
};
export default Filters;